import request from "./request";
import type {
  ICreation,
  ICredit,
  DetectData,
  Face,
  GcsSign,
  Material,
  IPayment,
  Product,
  FaceSwapRes,
  Tool,
  UserInfo,
  PaymentMethod,
} from "~/types";
class Api {
  async userinfo(): Promise<UserInfo> {
    return request.post("/my/userinfo");
  }

  async logout(): Promise<{ ok: boolean }> {
    return request.post("/auth/logout");
  }

  // 获取产品列表 — 仅返回一次性积分包
  async getProducts(): Promise<Product[]> {
    return request.post("/payment/products");
  }

  // 购买商品 — 5 个支付商统一入口
  // method 取值: voltpay | payblis | plisio | luxury | telegram
  // luxury 时可额外传 paymentMethod = card | paypal | venmo | applepay
  async submitOrder(
    product: Product,
    method: PaymentMethod,
    paymentMethod?: string,
    email?: string,
  ): Promise<{ url: string; callbackId: string; provider: string; payChannel: string }> {
    return request.post("/checkout/order", {
      product_id: product.product_id,
      provider: method,
      payment_method: paymentMethod,
      email,
      user_origin: window.location.origin,
    });
  }

  // 支付历史记录
  async getPaymentList(
    page: number = 1,
    perPage: number = 20
  ): Promise<IPayment[]> {
    return request.post("/my/payment_lists", { page, per_page: perPage });
  }

  // 积分历史记录
  async getCreditList(
    page: number = 1,
    perPage: number = 20
  ): Promise<ICredit[]> {
    return request.post("/my/credit_lists", { page, per_page: perPage });
  }

  // 人脸列表
  async getFaceList(page: number = 1, perPage: number = 20): Promise<Face[]> {
    return request.post("/my/faces", { page, per_page: perPage });
  }

  // 置顶人脸
  async topedFace(faceId: string): Promise<null> {
    return request.post("/toped/face", { face_id: faceId });
  }

  // 删除人脸
  async deleteFace(faceId: string): Promise<null> {
    return request.post("/del/face", { face_id: faceId });
  }

  // 上传人脸
  async uploadFace(photo: File) {
    const formData = new FormData();
    formData.append("photo", photo);

    return request.post("/upload/face", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  }

  // 获取签名 url format support: 'png' | 'jpg' | 'jpeg' | 'mp4'
  async getSignUrl(format: string, resumable: number = 0): Promise<GcsSign> {
    return request.post("/upload/sign_url", { format, resumable, user_origin: window.location.origin });
  }

  // R2 上传完成后写库,使 /my/uploads 和编辑器最近列表能看到
  async confirmUpload(payload: {
    material_id: string;
    url: string;
    format?: string;
    size?: number;
    width?: number;
    height?: number;
    duration?: number;
  }): Promise<{ material_id: string; url: string }> {
    return request.post("/upload/confirm", payload);
  }

  // 我的素材
  async getMyUploads(
    type: string = "all",
    page: number = 1,
    perPage: number = 20
  ): Promise<{ lists: Material[]; total: number }> {
    return request.post("/my/uploads", { type, page, per_page: perPage });
  }

  // 删除素材
  async deleteMat(materialId) {
    return request.post("/del/material", { material_id: materialId });
  }

  // 我的创作
  async getMyCreations(
    type: string = "all",
    page: number = 1,
    perPage: number = 20
  ): Promise<{ lists: ICreation[]; total: number }> {
    return request.post("/my/creates", { type, page, per_page: perPage });
  }

  // 提交人脸识别
  async uploadDetectCreate(
    { materialId, trimInterval, useFacefusion }: {
      materialId: string,
      trimInterval?: string,
      useFacefusion?: boolean
    }
  ): Promise<{ material_id: string; task_id: string }> {
    const data: { material_id: string, trim_interval?: string, use_facefusion?: boolean } = { material_id: materialId };
    if (trimInterval) {
      data.trim_interval = trimInterval;
    }
    if (useFacefusion !== undefined) {
      data.use_facefusion = useFacefusion;
    }
    return request.post("/upload/detect_create", data);
  }

  // 获取人脸识别结果
  async uploadDetectResult(
    materialId: string,
    taskId?: string
  ): Promise<DetectData> {
    const data: { material_id: string; task_id?: string } = {
      material_id: materialId,
    };
    if (taskId) {
      data.task_id = taskId;
    }
    return request.post("/upload/detect_result", data);
  }

  // 删除作品
  async deleteCreation(orderId: string) {
    return request.post("/del/order", { order_id: orderId });
  }

  // 提交换脸
  async createFaceSwap(
    materialId: string,
    bindFace: Array<{ detected_face_url: string; user_face_id: string }>,
    toolId: string,
    swapMode?: 'single' | 'multiple'
  ): Promise<{ order_id: string; task_id: string; wait_interval: number }> {
    // 新格式：数组结构 [{"detected_face_url": "...", "user_face_id": "..."}]
    const bindFaceString = JSON.stringify(bindFace);

    return request.post("/faceswap/create", {
      tool_id: toolId || "P001",
      material_id: materialId,
      bind_face: bindFaceString,
      swap_mode: swapMode || 'single', // 默认为 single (V1.0)
    });
  }

  // 获取换脸结果
  async fetchFaceSwapRes(
    orderId: string,
    taskId: string
  ): Promise<FaceSwapRes> {
    return request.post("/faceswap/result", {
      order_id: orderId,
      task_id: taskId,
    });
  }

  // 通过material获取素材
  async getMaterialDetail(
    materialId: string,
    toolId: string
  ): Promise<Material | null> {
    return request.post("/upload/material_detail", {
      material_id: materialId,
      tool_id: toolId,
    });
  }

  // 通过material获取素材
  async getTools(): Promise<Tool[] | null> {
    return request.post("/tool/lists");
  }

  // ===== Sex Pose（基于 Qwen Rapid AIO 图像生成）=====
  async createSexPose(payload: {
    image_urls: string[];
    effect_id?: string;
    prompt?: string;
    photoreal?: boolean;
    ultra_detail?: boolean;
    quality?: 'HD' | 'FHD';
    width?: number;
    height?: number;
  }): Promise<{ order_id: string; task_id: string; wait_interval: number }> {
    return request.post("/sex-pose/create", payload);
  }
  async fetchSexPoseRes(orderId: string, taskId: string): Promise<{
    order_id: string; url: string; task_status: 0 | 1 | -1; task_id: string; message?: string;
  }> {
    return request.post("/sex-pose/result", { order_id: orderId, task_id: taskId });
  }

  // ===== AI Video（Image-to-Video，基于 AiPose Video 4.0）=====
  async createAiVideo(payload: {
    image_url: string;
    effect_id?: string;
    prompt?: string;
    aspect_ratio?: string;
    resolution?: 'HD' | 'FHD';
    duration?: '5' | '10';
  }): Promise<{ order_id: string; task_id: string; wait_interval: number }> {
    return request.post("/ai-video/create", payload);
  }
  async fetchAiVideoRes(orderId: string, taskId: string): Promise<{
    order_id: string; url: string; task_status: 0 | 1 | -1; task_id: string; message?: string;
  }> {
    return request.post("/ai-video/result", { order_id: orderId, task_id: taskId });
  }

  async oneTapLogin(data: {
    idtoken: string;
    utmAll;
    redirect?: string;
  }): Promise<{ uid: string; token: string }> {
    return request.post("/idtoken_login/google", {
      idtoken: data.idtoken,
      utm_all: data.utmAll,
      redirect: data.redirect,
    });
  }

  // 邮箱注册
  async registerByEmail(
    email: string,
    password: string,
    utmAll?: string
  ): Promise<any> {
    const payload = {
      email,
      password,
      ...(utmAll && { utm_all: utmAll }),
    };
    return request.post("/email/register", payload);
  }
  // 邮箱登录
  async loginByEmail(
    email: string,
    password: string
  ): Promise<{ token: string }> {
    return request.post("/email/login", { email, password });
  }

  // // 修改密码
  // async changePassword(currPwd: string, newPwd: string, newPwdAgain: string): Promise<any> {
  //     return request.post('/email/change_password', {current_password: currPwd, new_password: newPwd, new_password_confirmation: newPwdAgain})
  // }

  // 修改密码接口 - 参数为可选
  async changePassword(payload: {
    current_password?: string;
    new_password: string;
    new_password_confirmation: string;
  }): Promise<any> {
    return request.post("/email/change_password", payload);
  }

  // 激活邮箱：用 register 时发送的 6 位验证码换 session
  async emailActivate(email: string, code: string): Promise<{ token: string }> {
    return request.get(
      `/email/activate?email=${encodeURIComponent(email)}&code=${encodeURIComponent(code)}`,
    );
  }

  // 忘记密码
  async forgotPassword(email: string): Promise<any> {
    return request.post("/email/forgot_password", { email });
  }

  // 忘记密码校验：仅检查验证码是否有效，不消费
  async forgotVerify(email: string, code: string): Promise<any> {
    return request.get(
      `/email/forgot_verify?email=${encodeURIComponent(email)}&code=${encodeURIComponent(code)}`,
    );
  }

  // 重置密码
  async resetPassword(
    email: string,
    code: string,
    newPwd: string,
    newPwdAgain: string
  ): Promise<any> {
    return request.post("/email/reset_password", {
      email,
      code,
      new_password: newPwd,
      new_password_confirmation: newPwdAgain,
    });
  }

  // 删除用户帐号
  async deleleAccount(uid: string): Promise<any> {
    return request.post("/del/account", { uid });
  }

  // 提交用户反馈
  async feedback(
    contents: string,
    attachments: string = "",
    email: string = "",
    rating?: string,
    source?: string,
    extra?: { issue_type?: string; payment_method?: string; amount?: string },
  ): Promise<any> {
    return request.post("/feedback/create", {
      contents, attachments, email, rating, source,
      issue_type: extra?.issue_type,
      payment_method: extra?.payment_method,
      amount: extra?.amount,
    });
  }

  // 上传附件获取签名url
  async attachUpploader(
    format: string
  ): Promise<{ sign_url: string; file_url: string }> {
    return request.post("/feedback/sign_url", { format });
  }

  async reportExceptions(
    type: string,
    data: { message: string;[key: string]: any }
  ) {
    return request.post("/feedback/failed_event", {
      type,
      input_data: data,
    });
  }

  // 兑换码
  async redeemCode(redeemKey: string): Promise<{
    type: string;
    product_name: string;
    credits_amount: number;
    sub_days: number;
    sub_months: number;
    bonus_credits: number;
  }> {
    return request.post("/redeem/use", { redeem_key: redeemKey });
  }
}

const api = new Api();
export default api;
